import { Head, Link } from "@inertiajs/react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ManagedImage, SortableImageUploader } from "@/components/desk/SortableImageUploader";

interface Agency {
  id: number;
  name: string;
}

interface Region {
  id: number;
  name: string;
}

interface Property {
  id: number;
  title: string;
  kind?: string | null;
  contract_type?: string | null;
  delivery_type?: string | null;
  description?: string | null;
  price?: number | null;
  total_area?: number | null;
  liveable_area?: number | null;
  rooms_count?: number | null;
  bathroom_count?: number | null;
  floor?: string | null;
  construction_date?: string | null;
  latitude?: string | null;
  longitude?: string | null;
  is_featured?: boolean | null;
  likes_count?: number | null;
  views_count?: number | null;
  images?: ManagedImage[];
  created_at: string;
  updated_at: string;
  agency?: Agency | null;
  region?: Region | null;
}

interface Props {
  property: Property;
}

export default function Show({ property }: Props) {
  const formatPrice = (price: number | null | undefined) => {
    if (!price) return "—";
    return new Intl.NumberFormat("en-SA", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
    }).format(price);
  };

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return "—";
    const date = new Date(dateString);
    return new Intl.DateTimeFormat("ar-UK", {
      year: "numeric",
      month: "long",
      day: "numeric",
    }).format(date);
  };

  const formatYear = (dateString: string | null | undefined) => {
    if (!dateString) return "—";
    const match = dateString.match(/^\d{4}/);
    if (match) return match[0];
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return "—";
    return date.getFullYear().toString();
  };

  const infoItems = [
    { label: "الوكالة", value: property.agency?.name },
    { label: "المنطقة", value: property.region?.name },
    { label: "العنوان", value: property.title },
    { label: "النوع", value: property.kind },
    { label: "نوع العقد", value: property.contract_type },
    { label: "نوع التسليم", value: property.delivery_type },
    { label: "السعر", value: formatPrice(property.price) },
    { label: "المساحة الإجمالية", value: property.total_area ? `${property.total_area} م²` : null },
    {
      label: "المساحة القابلة للسكن",
      value: property.liveable_area ? `${property.liveable_area} م²` : null,
    },
    { label: "عدد الغرف", value: property.rooms_count?.toString() },
    { label: "عدد الحمامات", value: property.bathroom_count?.toString() },
    { label: "الطابق", value: property.floor },
    { label: "سنة البناء", value: formatYear(property.construction_date || undefined) },
    { label: "خط العرض", value: property.latitude },
    { label: "خط الطول", value: property.longitude },
    { label: "عقار مميز", value: property.is_featured ? "نعم" : "لا" },
    { label: "الاعجابات", value: property.likes_count?.toString() },
    { label: "المشاهدات", value: property.views_count?.toString() },
    { label: "تاريخ الإنشاء", value: formatDate(property.created_at) },
    { label: "تاريخ التحديث", value: formatDate(property.updated_at) },
  ];

  return (
    <>
      <Head title={`تفاصيل العقار`} />

      <Card>
        <CardHeader className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
          <div>
            <CardTitle>تفاصيل العقار</CardTitle>
            <CardDescription>معلومات العقار الأساسية</CardDescription>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" asChild>
              <Link href="/desk/properties">عودة للقائمة</Link>
            </Button>
            <Button asChild>
              <Link href={`/desk/properties/${property.id}/edit`}>تعديل العقار</Link>
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          <SortableImageUploader
            images={property.images || []}
            uploadPath={`/desk/properties/${property.id}/images`}
            reorderPath={`/desk/properties/${property.id}/images/reorder`}
            deletePath={(imageId) => `/desk/properties/${property.id}/images/${imageId}`}
          />

          {property.description && (
            <div>
              <h3 className="text-sm font-semibold text-muted-foreground">الوصف</h3>
              <p className="mt-2 text-sm leading-6 text-foreground">{property.description}</p>
            </div>
          )}

          <div className="grid gap-4 md:grid-cols-2">
            {infoItems.map((item) => {
              if (!item.value) {
                return null;
              }

              return (
                <div key={item.label} className="rounded-lg border bg-muted/40 p-4">
                  <p className="text-xs font-medium text-muted-foreground">{item.label}</p>
                  <div className="mt-2 text-sm text-foreground">{item.value}</div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </>
  );
}
